package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.shared.GlossaryTermDto
import dev.lingvo.shared.GlossaryTranslationDto
import dev.lingvo.shared.UpsertGlossaryTermRequest
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LChip
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LPlusIcon
import dev.lingvo.site.ds.LSquarePenIcon
import dev.lingvo.site.ds.LSwitch
import dev.lingvo.site.ds.LToggleChip
import dev.lingvo.site.ds.LTrashIcon
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.runProjectAction
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private data class GlossaryDraft(
    val term: String = "",
    val description: String = "",
    val caseSensitive: Boolean = false,
    val doNotTranslate: Boolean = false,
    val translations: Map<String, String> = emptyMap(),
)

@Composable
internal fun GlossaryContent(
    data: GlossaryPageData,
    authority: ProjectAuthority,
    actionStateKey: Any? = data.project.id to authority.role,
    mutationClaim: ProjectActionClaim? = null,
    isMutationCurrent: () -> Boolean = { authority.canEditStructure },
    slug: String,
    onChanged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    currentMemberships: () -> List<dev.lingvo.shared.WorkspaceDto> = { Session.organizations },
    createTerm: suspend (String, UpsertGlossaryTermRequest) -> Unit = { projectSlug, request ->
        ApiClient.createGlossaryTerm(projectSlug, request)
    },
    updateTerm: suspend (String, String, UpsertGlossaryTermRequest) -> Unit = { projectSlug, id, request ->
        ApiClient.updateGlossaryTerm(projectSlug, id, request)
    },
    deleteTerm: suspend (String, String) -> Unit = { projectSlug, id ->
        ApiClient.deleteGlossaryTerm(projectSlug, id)
    },
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
) {
    val scope = rememberCoroutineScope()
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }
    }
    val locales = data.project.locales.filter { it != data.project.sourceLocale }
    val canEdit = authority.canEditStructure
    var draft by remember(actionStateKey) { mutableStateOf(GlossaryDraft()) }
    var adding by remember(actionStateKey) { mutableStateOf(false) }
    var editing by remember(actionStateKey) { mutableStateOf<GlossaryTermDto?>(null) }
    var deleting by remember(actionStateKey) { mutableStateOf<GlossaryTermDto?>(null) }
    var busy by remember(actionStateKey) { mutableStateOf(false) }
    var previouslyCouldEdit by remember(data.project.id, slug) { mutableStateOf(canEdit) }

    LaunchedEffect(canEdit) {
        if (previouslyCouldEdit && !canEdit) {
            adding = false
            editing = null
            deleting = null
            (document.getElementById("project-glossary-readonly-$slug") as? HTMLElement)?.focus()
        }
        previouslyCouldEdit = canEdit
    }

    fun mutate(success: String, failure: String, after: () -> Unit = {}, block: suspend () -> Unit) {
        if (!canEdit || !isMutationCurrent()) return
        val claim = mutationClaim
            ?.takeIf { it.remainsAuthorized(currentRoute(), currentMemberships()) }
            ?: return
        launch {
            if (!isMutationCurrent()) return@launch
            runProjectAction(
                claim = claim,
                currentRoute = currentRoute,
                currentMemberships = currentMemberships,
                setBusy = { busy = it },
                transport = block,
                onSuccess = {
                    after()
                    notify(success, LToastVariant.SUCCESS, 4_000)
                    onChanged()
                },
                onFailure = { error ->
                    notify(error.safeGlossaryMessage(failure), LToastVariant.DANGER, TOAST_UNTIL_DISMISSED)
                },
            )
        }
    }

    val openAdd: () -> Unit = {
        if (canEdit && isMutationCurrent()) {
            draft = GlossaryDraft(translations = locales.associateWith { "" })
            adding = true
        }
    }
    PageHeader(
        title = t("app.projects.slug.glossary.glossary", "Glossary"),
        // The hand-written rule got its own language wrong at one — "1 term guide human and AI
        // translation" — before it got every other language wrong.
        subtitle = tPlural(
            "app.projects.slug.glossary.count.term_guides",
            data.terms.size,
            "{count} term guides human and AI translation.",
            "{count} terms guide human and AI translation.",
        ),
    ) {
        // **One primary per screen.** With no terms yet, the empty state below is the invitation
        // and carries the accent; this header rendered a second identical "Add term" beside it,
        // opening the same modal, which reads as if the two offered different things.
        if (canEdit && data.terms.isNotEmpty()) {
            LButton(
                t("app.projects.slug.glossary.add_term", "Add term"),
                openAdd,
                leadingIcon = { LPlusIcon() },
            )
        }
    }
    if (!canEdit) {
        Div(attrs = {
            id("project-glossary-readonly-$slug")
            attr("tabindex", "-1")
        }) {
            LAlert(
                t("app.projects.slug.glossary.you_can_read_these_but_not", "Glossary terms are managed by an owner or admin"),
                t("app.projects.slug.glossary.a_glossary_term_is_a_rule_2", "A glossary term is a rule the whole team translates against, so owners and admins set " +
                    "them. Knowing which words are fixed is the point of showing you."),
                variant = LAlertVariant.INFO,
            )
        }
    }
    if (data.terms.isEmpty()) {
        LEmptyState(
            title = t("app.projects.slug.glossary.build_a_shared_language", "Build a shared language"),
            description = t("app.projects.slug.glossary.glossary_terms_keep_product_language_consistent", "Glossary terms keep product language consistent across locales and give AI translation the terminology it must follow."),
            icon = LEmptyStateIcon.LANGUAGE,
        ) {
            if (canEdit) LButton(t("app.projects.slug.glossary.add_term", "Add term"), openAdd)
        }
    } else {
        GlossaryTable(
            terms = data.terms,
            locales = locales,
            canEdit = canEdit,
            busy = busy,
            onEdit = { term ->
                if (canEdit && isMutationCurrent()) {
                    editing = term
                    draft = GlossaryDraft(
                        term = term.term,
                        description = term.description.orEmpty(),
                        caseSensitive = term.caseSensitive,
                        doNotTranslate = term.doNotTranslate,
                        translations = locales.associateWith { locale ->
                            term.translations.firstOrNull { it.locale == locale }?.value.orEmpty()
                        },
                    )
                }
            },
            onDelete = { if (canEdit && isMutationCurrent()) deleting = it },
        )
    }
    if (canEdit) {
        GlossaryModal(adding, null, locales, draft, { draft = it }, { adding = false }, busy) {
            mutate(
                tr("app.projects.slug.glossary.glossary_term_added", "Glossary term added"),
                tr("app.projects.slug.glossary.couldnt_add_this_glossary_term_retry", "Couldn't add this glossary term. Retry?"),
                after = { adding = false },
            ) {
                createTerm(slug, draft.toRequest(locales))
            }
        }
        GlossaryModal(editing != null, editing, locales, draft, { draft = it }, { editing = null }, busy) {
            val term = editing ?: return@GlossaryModal
            mutate(
                tr("app.projects.slug.glossary.glossary_term_saved", "Glossary term saved"),
                tr("app.projects.slug.glossary.couldnt_save_this_glossary_term_retry", "Couldn't save this glossary term. Retry?"),
                after = { editing = null },
            ) {
                updateTerm(slug, term.id, draft.toRequest(locales))
            }
        }
        // A plain yes/no, so the design system's own confirmation rather than a hand-rolled modal:
        // it owns the focus return, the busy label and the danger tone in one place.
        LConfirmDialog(
            open = deleting != null,
            title = t("app.projects.slug.glossary.delete_glossary_term", "Delete glossary term?"),
            confirmLabel = t("app.projects.slug.glossary.delete_term", "Delete term"),
            onConfirm = {
                deleting?.let { term ->
                    mutate(
                        tr("app.projects.slug.glossary.glossary_term_deleted", "Glossary term deleted"),
                        tr(
                            "app.projects.slug.glossary.couldnt_delete_this_glossary_term_retry",
                            "Couldn't delete this glossary term. Retry?",
                        ),
                        after = { deleting = null },
                    ) { deleteTerm(slug, term.id) }
                }
            },
            onCancel = { deleting = null },
            tone = LConfirmTone.DANGER,
            consequenceTitle = t(
                "app.projects.slug.glossary.this_cant_be_undone",
                "This can't be undone",
            ),
            consequence = tf(
                "app.projects.slug.glossary.delete_term_confirm",
                "Removing “{term}” stops it guiding human and AI translation from now on. The " +
                    "translations already written against it stay as they are.",
                "term" to deleting?.term.orEmpty(),
            ),
            busy = busy,
        )
    }
}

/**
 * The terms, with the rules on the term rather than in columns of their own.
 *
 * **Two columns of disabled switches were two columns of nothing.** "Do not translate" and "Case
 * sensitive" are false on almost every row, so the table spent a third of its width drawing "off"
 * over and over, and a screen reader read two inert form controls per row. They are badges on the
 * term now, present only when true — which is also the only time they mean anything.
 *
 * **The translations are one cell, not one column per language.** A project with nine target
 * languages had a table nobody could read without scrolling sideways past the term it was about.
 * Past four languages a filter appears, because at that point choosing is cheaper than scanning.
 */
@Composable
private fun GlossaryTable(
    terms: List<GlossaryTermDto>,
    locales: List<String>,
    canEdit: Boolean,
    busy: Boolean,
    onEdit: (GlossaryTermDto) -> Unit,
    onDelete: (GlossaryTermDto) -> Unit,
) {
    var shownLocales by remember(locales) { mutableStateOf(locales.toSet()) }
    val filtering = locales.size > 4
    val visibleLocales = if (filtering) locales.filter { it in shownLocales } else locales

    if (filtering) {
        Row(Modifier.fillMaxWidth().gap(Space.XS).flexWrap(FlexWrap.Wrap)) {
            locales.forEach { locale ->
                LToggleChip(
                    text = localeInfo(locale).englishName,
                    selected = locale in shownLocales,
                    onSelectedChange = { on ->
                        shownLocales = if (on) shownLocales + locale else shownLocales - locale
                    },
                )
            }
        }
    }

    // The caption names the table for assistive technology and must stay; it is not painted,
    // because "Project glossary" a hundred pixels under the H1 "Glossary" is the page identity
    // said a second time, more quietly, in a full 56px band above the header row.
    LTable(
        caption = t("app.projects.slug.glossary.project_glossary", "Project glossary"),
        captionHidden = true,
    ) {
        LTableHead {
            LTableRow {
                LTableHeaderCell { Text(t("app.projects.slug.glossary.term", "Term")) }
                LTableHeaderCell { Text(t("app.projects.slug.glossary.description_column", "Description")) }
                LTableHeaderCell { Text(t("app.projects.slug.glossary.translations", "Translations")) }
                LTableHeaderCell { Text(t("app.projects.slug.glossary.actions", "Actions")) }
            }
        }
        LTableBody {
            terms.forEach { term ->
                LTableRow {
                    LTableCell {
                        Column(Modifier.gap(Space.XXS)) {
                            Text(term.term)
                            Row(Modifier.gap(Space.XXS).flexWrap(FlexWrap.Wrap)) {
                                if (term.doNotTranslate) {
                                    LBadge(
                                        t("app.projects.slug.glossary.do_not_translate", "Do not translate"),
                                        variant = LBadgeVariant.WARNING,
                                    )
                                }
                                if (term.caseSensitive) {
                                    LBadge(
                                        t("app.projects.slug.glossary.case_sensitive", "Case sensitive"),
                                        variant = LBadgeVariant.NEUTRAL,
                                    )
                                }
                            }
                        }
                    }
                    LTableCell { Text(term.description?.takeIf(String::isNotBlank) ?: "—") }
                    LTableCell {
                        val written = visibleLocales.mapNotNull { locale ->
                            term.translations
                                .firstOrNull { it.locale == locale }
                                ?.value
                                ?.takeIf(String::isNotBlank)
                                ?.let { locale to it }
                        }
                        if (written.isEmpty()) {
                            Text("—")
                        } else {
                            Row(Modifier.gap(Space.XXS).flexWrap(FlexWrap.Wrap)) {
                                written.forEach { (locale, value) ->
                                    LChip(
                                        trf(
                                            "app.projects.slug.glossary.locale_and_translation",
                                            "{locale}: {value}",
                                            "locale" to locale,
                                            "value" to value,
                                        ),
                                    )
                                }
                            }
                        }
                    }
                    LTableCell {
                        if (canEdit) {
                            // Every row's pair reads "Edit" and "Delete"; on a glossary of thirty
                            // terms a screen reader's control list was sixty identical entries with
                            // nothing naming the term.
                            Row(Modifier.gap(Space.XS)) {
                                LIconAction(
                                    label = trf(
                                        "app.projects.slug.glossary.edit_term",
                                        "Edit {term}",
                                        "term" to term.term,
                                    ),
                                    onClick = { onEdit(term) },
                                    size = LButtonSize.SM,
                                    enabled = !busy,
                                    icon = { LSquarePenIcon() },
                                )
                                LIconAction(
                                    label = trf(
                                        "app.projects.slug.glossary.delete_term_named",
                                        "Delete {term}",
                                        "term" to term.term,
                                    ),
                                    onClick = { onDelete(term) },
                                    // Ghost, like its Edit sibling. A filled `danger` square on
                                    // every row stacked into a red column louder than "Add term",
                                    // the page's one primary action — and semantic colour attaches
                                    // to state, not to an action. The destructive weight belongs on
                                    // the confirmation, which already carries `LConfirmTone.DANGER`.
                                    size = LButtonSize.SM,
                                    enabled = !busy,
                                    icon = { LTrashIcon() },
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun GlossaryModal(
    open: Boolean,
    editing: GlossaryTermDto?,
    locales: List<String>,
    draft: GlossaryDraft,
    onDraft: (GlossaryDraft) -> Unit,
    onClose: () -> Unit,
    busy: Boolean,
    onSave: () -> Unit,
) {
    LModal(
        open = open,
        title = if (editing == null) t("app.projects.slug.glossary.add_glossary_term", "Add glossary term") else t("app.projects.slug.glossary.edit_glossary_term", "Edit glossary term"),
        onClose = onClose,
        actions = {
            LButton(t("app.projects.slug.glossary.cancel", "Cancel"), onClose, variant = LButtonVariant.GHOST, enabled = !busy)
            LButton(
                label = if (editing == null) t("app.projects.slug.glossary.add_term", "Add term") else t("app.projects.slug.glossary.save_changes", "Save changes"),
                onClick = onSave,
                enabled = draft.term.isNotBlank() && !busy,
                loading = busy,
            )
        },
    ) {
        LTextField(draft.term, { onDraft(draft.copy(term = it)) }, label = t("app.projects.slug.glossary.source_term", "Source term"), placeholder = t("app.projects.slug.glossary.checkout", "Checkout"))
        LTextArea(
            value = draft.description,
            onValueChange = { onDraft(draft.copy(description = it)) },
            label = t("app.projects.slug.glossary.description", "Description"),
            placeholder = t("app.projects.slug.glossary.explain_when_and_how_this_term", "Explain when and how this term is used"),
        )
        LSwitch(
            checked = draft.doNotTranslate,
            onCheckedChange = { onDraft(draft.copy(doNotTranslate = it)) },
            label = t("app.projects.slug.glossary.do_not_translate", "Do not translate"),
        )
        LSwitch(
            checked = draft.caseSensitive,
            onCheckedChange = { onDraft(draft.copy(caseSensitive = it)) },
            label = t("app.projects.slug.glossary.case_sensitive", "Case sensitive"),
        )
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            locales.forEach { locale ->
                val info = localeInfo(locale)
                LTextField(
                    value = draft.translations[locale].orEmpty(),
                    onValueChange = { value -> onDraft(draft.copy(translations = draft.translations + (locale to value))) },
                    label = tf("app.projects.slug.glossary.language_and_code", "{language} — {code}", "language" to info.englishName, "code" to locale),
                    placeholder = t("app.projects.slug.glossary.translation", "Translation"),
                    enabled = !draft.doNotTranslate,
                )
            }
        }
    }
}

private fun GlossaryDraft.toRequest(locales: List<String>) = UpsertGlossaryTermRequest(
    term = term.trim(),
    description = description.trim().ifBlank { null },
    caseSensitive = caseSensitive,
    doNotTranslate = doNotTranslate,
    translations = if (doNotTranslate) emptyList() else locales.mapNotNull { locale ->
        translations[locale]?.trim()?.takeIf(String::isNotBlank)?.let { GlossaryTranslationDto(locale, it) }
    },
)

private fun Throwable.safeGlossaryMessage(fallback: String): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank) ?: fallback
